/**
 * Extra-scoped virtual views (B3): one de-prefixed view per narrow
 * `*_Agent` source. Eleven narrow, single-concern surfaces —
 * eight network/field/reservoir/event-grain RESHAPE views (no per-string
 * ancestry) and three per-string ENRICHED WellTest views (ancestry resolved off
 * the `wellString` ref).
 *
 * Factory choice follows the source grain:
 *   - PER-STRING (a `wellString` direct relation): the shared
 *     `makeStringContextViews` factory — `keep: STRING_KEEP` +
 *     `enrich: STRING_CONTEXT_ENRICH`, so each row carries its
 *     well/field/reservoir StringContext.
 *   - NETWORK / FIELD / RESERVOIR / EVENT grain (no per-string ancestry):
 *     `createReshapedView` with `keep: IDENTITY + grain relation`. Pure reshape.
 *
 * Each de-prefixes its source measure family to canonical names via the shared
 * shapes (the SSOT for column suffix/rename/docs), keeping identity + the grain
 * relation(s) so the agent can scope by / join the grain entity. The whole set
 * is built per session because the enriched WellTest views close over the CDF
 * client + hierarchy/entity views.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import { describeAncestry, describeStringScope, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  ACTUAL_OUTPUT,
  CHOKE_OUTPUTS,
  FIELD_ALLOCATION_FACTORS,
  FIELD_GAS_COMPOSITION,
  FIELD_RELATION,
  FIELD_RELATION_DIRECT,
  FLOWLINE_OUTPUTS,
  FLOWLINE_OUTPUTS_BARE,
  FLOWLINE_OUTPUTS_RELATION,
  IDENTITY,
  INJECTION_HEADER_OUTPUTS,
  NETWORK_ENTITY_RELATION,
  NETWORK_RELATION_DIRECT,
  PHASE_RATES,
  POTENTIAL_OUTPUT,
  PUMP_OUTPUTS_NETWORK,
  RESERVOIR_INPUTS,
  RESERVOIR_RELATION_DIRECT,
  type Shape,
  TARGET_OUTPUT_INPUTS,
  TICKET_REGISTRY,
  WELLTEST_ESTIMATED_RATES,
  WELLTEST_INDEX_PRESSURE,
  WELLTEST_LAST_TEST,
  WELLTEST_MODEL_PARAMETERS,
  WELLTEST_MODEL_SCALARS,
  WELLTEST_RESERVOIR_EXTRAS,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import {
  AllocationFactorsFieldAgentView,
  ChokeOutputsNetworkAgentView,
  FieldInputsAgentView,
  FlowlineOutputsNetworkAgentView,
  InjectionHeaderOutputsNetworkAgentView,
  PumpOutputsNetworkAgentView,
  ReservoirInputsAgentView,
  TicketRegistryAgentView,
  WellTestAnalysisAgentView,
  WellTestModelParametersAgentView,
  WellTestReservoirAnalysisAgentView,
} from '#schema/generated/ipso/views.ts';

/** One reshape (non-per-string) surface: name, doc, source, kept grain ref(s), and de-prefixed groups. */
type ReshapeSpec = ViewFamilySpec & { readonly source: DataModelView; readonly relation: Shape };

/**
 * The eleven network/field/reservoir/event-grain reshape surfaces, declared as
 * data. Each keeps identity + its grain relation(s) and de-prefixes its measure
 * family. The choke / injection-header / pump network sources strip the `nmv`
 * network-matched-value marker; the flowline source mixes `nmv*` (de-marked,
 * reusing `PHASE_RATES` for the oil/gas/water rates) with three bare columns;
 * the field-input concern sub-views (Actual/Potential/Target/GasComposition) and
 * the reservoir-input and ticket sources carry no marker (bare passthrough).
 */
const RESHAPE_SPECS: readonly ReshapeSpec[] = [
  {
    name: 'AllocationFactorsField',
    description:
      'Field-level production/injection allocation factors (FIELD grain): preferred-VFM and weighted-average-VFM per-phase production allocation factors plus the gas/water injection allocation factors. Filter by field (or join the field entity) to scope to a field.',
    source: AllocationFactorsFieldAgentView,
    relation: FIELD_RELATION_DIRECT,
    groups: [{ shape: FIELD_ALLOCATION_FACTORS }],
  },
  {
    name: 'ChokeOutputs',
    description:
      'Choke-level outputs from the GAP network simulation (NETWORK grain): the simulated choke bean size. Filter by network (or join the network entity) to scope to a network; entityName references the choke entity.',
    source: ChokeOutputsNetworkAgentView,
    relation: [...NETWORK_RELATION_DIRECT, ...NETWORK_ENTITY_RELATION],
    groups: [{ shape: CHOKE_OUTPUTS, prefix: 'nmv' }],
  },
  {
    name: 'FlowlineOutputs',
    description:
      'Flowline-level outputs from the GAP network simulation (NETWORK grain): simulated/measured pressures and differential pressure, per-phase flow + injection rates, fluid ratios (GOR/GLR/WGR/CGR/water cut), velocities, C-factor, connected-manifold ids, and the flow-assurance / constraint flags. Filter by network (or join the network entity) to scope to a network; pipelineEntity references the flowline.',
    source: FlowlineOutputsNetworkAgentView,
    relation: FLOWLINE_OUTPUTS_RELATION,
    groups: [
      { shape: PHASE_RATES, prefix: 'nmv' },
      { shape: FLOWLINE_OUTPUTS, prefix: 'nmv' },
      { shape: FLOWLINE_OUTPUTS_BARE },
    ],
  },
  {
    name: 'InjectionHeaderOutputs',
    description:
      'Injection-header outputs from the GAP network simulation (NETWORK grain): measured vs estimated gas/water injection rate, pressure and temperature, plus the gas/water injection flow-measurement indices. Filter by network (or join the network entity) to scope to a network; entityName references the injection-header entity.',
    source: InjectionHeaderOutputsNetworkAgentView,
    relation: [...NETWORK_RELATION_DIRECT, ...NETWORK_ENTITY_RELATION],
    groups: [{ shape: INJECTION_HEADER_OUTPUTS, prefix: 'nmv' }],
  },
  {
    name: 'PumpOutputs',
    description:
      'Pump-level outputs from the GAP network simulation (NETWORK grain): pump liquid throughput rate and the violating-constraints flag. Filter by network (or join the network entity) to scope to a network; entityName references the pump entity.',
    source: PumpOutputsNetworkAgentView,
    relation: [...NETWORK_RELATION_DIRECT, ...NETWORK_ENTITY_RELATION],
    groups: [{ shape: PUMP_OUTPUTS_NETWORK, prefix: 'nmv' }],
  },
  {
    name: 'ActualOutputField',
    description:
      'Actual field production and injection (FIELD grain), de-prefixed to clean per-phase column names: oil/gas/water production and gas/water injection volumes (oil, gas, water, gasInjection, waterInjection) and rates (oilRate, gasRate, waterRate, gasInjectionRate, waterInjectionRate). Filter by fieldEntity (or join the field entity) to scope to a field.',
    source: FieldInputsAgentView,
    relation: FIELD_RELATION,
    groups: [{ shape: ACTUAL_OUTPUT }],
  },
  {
    name: 'PotentialOutputField',
    description:
      'Unconstrained potential field production and injection VOLUMES (FIELD grain), de-prefixed to clean per-phase column names (oil, gas, water, gasInjection, waterInjection). Potentials are volumes, not rates. Filter by fieldEntity (or join the field entity) to scope to a field.',
    source: FieldInputsAgentView,
    relation: FIELD_RELATION,
    groups: [{ shape: POTENTIAL_OUTPUT }],
  },
  {
    name: 'TargetOutputField',
    description:
      'Planning-target field production and injection VOLUMES (FIELD grain), de-prefixed to clean per-phase column names (oil, gas, water, gasInjection, waterInjection). Targets are volumes, not rates. Filter by fieldEntity (or join the field entity) to scope to a field.',
    source: FieldInputsAgentView,
    relation: FIELD_RELATION,
    groups: [{ shape: TARGET_OUTPUT_INPUTS }],
  },
  {
    name: 'FieldGasComposition',
    description:
      'Field gas-composition and ambient inputs (FIELD grain): associated, non-associated, and non-ASR gas production / injection volumes and rates (associatedGas, nonAssociatedGas, nonAsrGasInjection, and their rates), plus ambient temperature. Filter by fieldEntity (or join the field entity) to scope to a field.',
    source: FieldInputsAgentView,
    relation: FIELD_RELATION,
    groups: [{ shape: FIELD_GAS_COMPOSITION }],
  },
  {
    name: 'ReservoirInputs',
    description:
      'Reservoir-level allocation-factor inputs (RESERVOIR grain): the production and injection per-phase allocation factors. Filter by reservoir (or join the reservoir entity) to scope to a reservoir.',
    source: ReservoirInputsAgentView,
    relation: RESERVOIR_RELATION_DIRECT,
    groups: [{ shape: RESERVOIR_INPUTS }],
  },
  {
    name: 'TicketRegistry',
    description:
      'Troubleshooting-ticket registry (EVENT grain): ticket identity, summary, lifecycle status/priority, reported/due/closed dates, assignment, narrative fields, classification, the associated entityName/entityType, and the opportunity flag. The entity association is the scalar string entityName (no direct relation). Filter by status / priority / entityName to scope tickets.',
    source: TicketRegistryAgentView,
    relation: [],
    groups: [{ shape: TICKET_REGISTRY }],
  },
];

/** One per-string enriched WellTest surface: name, doc, source, and de-prefixed groups. */
type WellTestSpec = ViewFamilySpec & { readonly source: DataModelView };

/**
 * The three per-string WellTest surfaces, declared as data. Each de-prefixes its
 * `model*` measure family over the producing-string grain and is enriched with
 * well/field/reservoir StringContext off the `wellString` ref. WellTestAnalysis
 * and WellTestReservoirAnalysis share the {@link WELLTEST_INDEX_PRESSURE}
 * sub-family (composed by spread) so the six injectivity/productivity-index +
 * reservoir-pressure columns are declared once.
 */
const WELL_TEST_SPECS: readonly WellTestSpec[] = [
  {
    name: 'WellTestAnalysis',
    description: `Per-string well-test analysis (string grain): model-estimated flowing rates + FBHP, the last well-test measured snapshot (CHP/WHP/GOR/water cut/PDG pressure/timestamp/rates), and the injectivity/productivity indices + reservoir pressure with their percent changes${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: WellTestAnalysisAgentView,
    groups: [
      { shape: WELLTEST_ESTIMATED_RATES, prefix: 'modelEstimated' },
      { shape: WELLTEST_LAST_TEST, prefix: 'modelWellTest' },
      { shape: WELLTEST_INDEX_PRESSURE, prefix: 'model' },
    ],
  },
  {
    name: 'WellTestModelParameters',
    description: `Per-string well-test model parameters (string grain): Prosper/VLP/IPR model parameters, match status, revision number, ESP/gas-lift well-test settings and their percent changes, plus the Prosper-model presence flag and external id (scalars)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: WellTestModelParametersAgentView,
    groups: [{ shape: WELLTEST_MODEL_PARAMETERS, prefix: 'model' }, { shape: WELLTEST_MODEL_SCALARS }],
  },
  {
    name: 'WellTestReservoirAnalysis',
    description: `Per-string well-test reservoir analysis (string grain): the injectivity/productivity indices + reservoir pressure with their percent changes, plus minimum stable rate, stability flag, analysis status, and the well-test timestamp${describeAncestry(STRING_CONTEXT_ENRICH.relations)}${describeStringScope(STRING_CONTEXT_ENRICH.stringRefColumn)}`,
    source: WellTestReservoirAnalysisAgentView,
    groups: [
      { shape: WELLTEST_INDEX_PRESSURE, prefix: 'model' },
      { shape: WELLTEST_RESERVOIR_EXTRAS, prefix: 'model' },
    ],
  },
];

/** The three per-string WellTest surfaces — the enriched arm, built through the shared StringContext factory off each spec's own source. */
const useWellTestViews = makeStringContextViews({
  source: (spec: WellTestSpec) => spec.source,
  specs: WELL_TEST_SPECS,
});

/**
 * Build the fourteen B3 extra-scoped views: the eleven pure reshape views plus
 * the three per-string WellTest views (StringContext-enriched through the shared
 * factory). Built per session so the set can be threaded into
 * `createAppVirtualViews`.
 */
export const useExtraScopedViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return [
    ...RESHAPE_SPECS.map((spec: ReshapeSpec) =>
      createReshapedView({
        name: spec.name,
        description: spec.description,
        source: spec.source,
        keep: [...IDENTITY, ...spec.relation],
        groups: spec.groups,
        client,
        mintSeries,
      }),
    ),
    ...useWellTestViews(),
  ];
};
